import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import { draw } from './crmCompanies';

/**
 * The prospecting history of a commercial, which Jobbiz does not keep yet:
 * the calls, appointments, inbound requests and sales of each day, and the
 * weekly reviews. The demo draws them from the person's id, so a commercial
 * keeps the same history from one load to the next (crmStatsApi.js).
 */

// The figures of a day, as admin/crm/stats/days sends them.
export const STATS_KEYS = [
  'calls',
  'pickedUp',
  'discussions',
  'rdvTaken',
  'rdvDone',
  'rdvNoShow',
  'inbound',
  'sales',
  'revenue',
  'revenueEstimated',
];

export const emptyStats = (day) => ({
  day,
  ...Object.fromEntries(STATS_KEYS.map((key) => [key, 0])),
});

export const addStats = (total, row) => ({
  ...total,
  ...Object.fromEntries(STATS_KEYS.map((key) => [key, total[key] + row[key]])),
});

const pick = (personId, salt, size) => draw({ _id: personId }, salt, size);
// An integer around `mean`: its floor, plus one now and then.
const around = (personId, salt, mean) =>
  Math.floor(mean + pick(personId, salt, 100) / 100);
const weekdayOf = (day) => fromIsoDay(day).getDay();

// Six months of history at least, so that « best week since » has a past.
const firstDayOf = (personId, now) =>
  isoDay(addDays(now, -7 * (26 + pick(personId, 'start', 30))));

// A sale gets its first invoice about a month later: until then its revenue
// is estimated from its timesheets.
const INVOICED_AFTER_DAYS = 30;

/** The drawn figures of one commercial on one day (nothing in the future). */
export const drawnDay = (personId, day, now) => {
  if (day > isoDay(now) || day < firstDayOf(personId, now))
    return emptyStats(day);
  const weekday = weekdayOf(day);
  // Some commercials call more than others, every day.
  const level = 0.8 + pick(personId, 'level', 5) / 10;
  let calls = 0;
  if (weekday >= 1 && weekday <= 5)
    calls = Math.round((14 + pick(personId, `${day}:calls`, 17)) * level);
  else if (weekday === 6 && pick(personId, `${day}:saturday`, 4) === 0)
    calls = 2 + pick(personId, `${day}:calls`, 5);
  // Today is under way: its calls so far.
  const share =
    day === isoDay(now)
      ? Math.min(1, Math.max(0, (now.getHours() - 8) / 9))
      : 1;
  calls = Math.round(calls * share);
  const pickedUp = Math.round(
    (calls * (35 + pick(personId, `${day}:picked`, 21))) / 100,
  );
  const discussions = Math.round(
    (pickedUp * (45 + pick(personId, `${day}:talks`, 26))) / 100,
  );
  const rdvTaken = around(
    personId,
    `${day}:taken`,
    (discussions * (10 + pick(personId, `${day}:rate`, 9))) / 100,
  );
  const workday = weekday >= 1 && weekday <= 5 && share > 0.6;
  const met = workday ? around(personId, `${day}:met`, 0.75 * level) : 0;
  const missed = met > 0 && pick(personId, `${day}:noShow`, 7) === 0 ? 1 : 0;
  const sales = workday && pick(personId, `${day}:sale`, 12) === 0 ? 1 : 0;
  const revenue = sales ? (2 + pick(personId, `${day}:amount`, 13)) * 1000 : 0;
  const age = Math.round(
    (fromIsoDay(isoDay(now)) - fromIsoDay(day)) / 86400000,
  );
  return {
    day,
    calls,
    pickedUp,
    discussions,
    rdvTaken,
    rdvDone: met - missed,
    rdvNoShow: missed,
    inbound: workday && pick(personId, `${day}:inbound`, 11) === 0 ? 1 : 0,
    sales,
    revenue,
    revenueEstimated: age < INVOICED_AFTER_DAYS ? revenue : 0,
  };
};

const VICTORIES = [
  'RDV décroché avec la Pharmacie du Port après trois relances.',
  'Un gérant m’a rappelé de lui-même pour un devis.',
  'Deux RDV pris le même matin.',
  'J’ai tenu ma liste d’appels tous les jours.',
  'Premier contrat signé avec un hôtel du sud.',
];
const CHALLENGES = [
  'Passer 10 appels de plus le mardi matin',
  'Poser une question ouverte avant chaque argument',
  'Relancer chaque devis sous 48 h',
  'Prendre 2 RDV avant mercredi',
  'Rappeler les prospects du mois dernier',
];
const SCALES = ['confidence', 'questioning', 'objections', 'energy'];
const REVIEWED_WEEKS = 30;

/**
 * The drawn reviews of a commercial, oldest first: most past weeks, always
 * the three before this one (a streak to show), never this week (still to
 * do on Friday).
 */
export const drawnReviews = (personId, now) => {
  const monday = addDays(fromIsoDay(isoDay(now)), -((now.getDay() + 6) % 7));
  const first = firstDayOf(personId, now);
  return Array.from({ length: REVIEWED_WEEKS }, (_, index) =>
    isoDay(addDays(monday, -7 * (REVIEWED_WEEKS - index))),
  )
    .filter(
      (week, index) =>
        week >= first &&
        (index >= REVIEWED_WEEKS - 3 ||
          pick(personId, `${week}:review`, 10) < 7),
    )
    .map((week) => {
      const friday = addDays(fromIsoDay(week), 4);
      friday.setHours(17, 30, 0, 0);
      return {
        id: `${personId}-${week}`,
        personId,
        weekStart: week,
        ...Object.fromEntries(
          SCALES.map((scale) => [
            scale,
            2 + pick(personId, `${week}:${scale}`, 4),
          ]),
        ),
        victory: VICTORIES[pick(personId, `${week}:victory`, VICTORIES.length)],
        challenge:
          CHALLENGES[pick(personId, `${week}:challenge`, CHALLENGES.length)],
        createdAt: friday.toISOString(),
      };
    });
};
